import { Dispatch, SetStateAction, Suspense, useEffect, useRef, useState } from 'react';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { QueryStudentsData } from './QueryStudentsData';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { useSelector } from 'react-redux';
import { selectUser } from '../../redux/reducers/userSlice';
import { InputText } from 'primereact/inputtext';
import { CompleteUser } from '../../custom-hooks/useGetUsersBySite';
import { Grade } from '../../models/Grade';
import QueryGetAllClasses from '../QueryGetAllClasses/QueryGetAllClasses';
import QueryGetAllGradeLevels from '../QueryGetAllGradeLevels/QueryGetAllGradeLevels';
import { PageInfo } from '../../models/PageInfo';
import { TargetGroup } from '../../models/TargetGroup';
import { useParams } from 'react-router-dom';
import { NO_ASSIGNED_ROADMAP } from '../ClassesRoster/ClassesRosterRightSide';
import { PiiTypes } from '../../analytics/dataMasking';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { MaskedInput } from '../MaskedData/MaskedInput';
import { ClassNameSection } from '../../models/Class';
import { SquarePen } from 'lucide-react';

export interface StudentsTableFilterState {
  first: number;
  rows: number;
  page?: number;
  pageCount: number;
  sortField: string;
  sortOrder: 1 | -1;
  totalPages: number;
  filters: Filters;
  multiSortMeta: null;
  prevPage: number;
}

interface Filters {
  name: string;
  emailAddress: string;
  sites: { id: string; name: string }[] | null;
  grades: { id: string; name: string }[] | null;
  classes: { id: string; name: string }[] | null;
  roadmaps: { id: string; name: string }[] | null;
  [key: string]: string | { id: string; name: string }[] | null;
}

const filterStateInitValue: StudentsTableFilterState = {
  first: 0,
  rows: 5,
  page: 0,
  pageCount: 0,
  sortField: 'name',
  sortOrder: 1,
  totalPages: 1,
  filters: {
    name: '',
    emailAddress: '',
    sites: [],
    grades: null,
    classes: null,
    roadmaps: null,
  },
  multiSortMeta: null,
  prevPage: 0,
};

interface Props {
  refetchStudentKey?: number;
  setSelectedStudent: Dispatch<SetStateAction<CompleteUser | undefined>>;
  setOpenEnrollStudent: Dispatch<SetStateAction<boolean>>;
  createdStudentEmail?: { id: string; email: string };
}

const StudentsDataTable = ({
  setSelectedStudent,
  setOpenEnrollStudent,
  refetchStudentKey,
  createdStudentEmail,
}: Props) => {
  const params = useParams();
  const userRedux = useSelector(selectUser);
  const [studentsList, setStudentList] = useState<CompleteUser[]>([]);
  const [filterState, setFilterState] = useState<StudentsTableFilterState>({
    ...filterStateInitValue,
    filters: {
      ...filterStateInitValue.filters,
      sites: userRedux?.sites?.map(st => ({ id: st.id || '', name: st.name || '' })) || null,
    },
  });
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [fetchingData, setFetchingData] = useState<boolean>(true);
  const [classes, setClasses] = useState<{ nodes: ClassNameSection[] }>({ nodes: [] });
  const [allGradeLevels, setAllGradeLevels] = useState<Grade[]>([]);
  const [studentsPagination, setStudentsPagination] = useState<
    Record<number, { endCursor?: string | null; startCursor?: string | null }>
  >({});

  const setStudentListWithEmailOverride: Dispatch<SetStateAction<CompleteUser[]>> = update => {
    setStudentList(prev => {
      const next = typeof update === 'function' ? (update as (p: CompleteUser[]) => CompleteUser[])(prev) : update;
      if (!createdStudentEmail) return next;
      return next.map(student =>
        student.id === createdStudentEmail.id && !student.emailAddress
          ? { ...student, emailAddress: createdStudentEmail.email }
          : student,
      );
    });
  };

  useEffect(() => {
    if (!firstLoad) setFetchingData(true);
  }, [refetchStudentKey]);
  const didPresetClassRef = useRef(false);

  useEffect(() => {
    if (didPresetClassRef.current) return;

    if (
      params.defaultClass &&
      classes.nodes.length > 0 &&
      classes?.nodes?.some(cl => cl?.id === `ClassNode:${params.defaultClass}`)
    ) {
      const preSetClass = classes.nodes.find(cl => cl?.id === `ClassNode:${params.defaultClass}`) as {
        id: string;
        name: string;
        section: string;
      };
      setFilterState(prev => ({
        ...prev,
        filters: {
          ...prev.filters,
          classes: preSetClass ? [preSetClass as { id: string; name: string; section: string }] : null,
        },
      }));
      didPresetClassRef.current = true;
    }
  }, [classes.nodes, params.defaultClass]);

  const rowFilterTemplate = (option: ColumnFilterElementTemplateOptions, options: { id: string; name: string }[]) => (
    <MultiSelect
      disabled={!options.length}
      name={option.field}
      optionLabel="name"
      value={(filterState.filters as Filters)[option.field]}
      options={options}
      itemTemplate={option => {
        return <span key={option.id}>{option?.section ? `${option.name} - ${option.section}` : option?.name}</span>;
      }}
      onChange={(e: MultiSelectChangeEvent) => {
        setFetchingData(true);
        setFilterState({
          ...filterState,
          filters: {
            ...filterState.filters,
            [e.target.name]: e.target.name === 'sites' ? e.target.value : e.target.value.length ? e.target.value : null,
          },
        });
      }}
      placeholder="Search"
      maxSelectedLabels={1}
      className="w-full mx-0 px-0"
    />
  );

  return (
    <>
      <Suspense>
        <QueryStudentsData
          filterState={filterState}
          setFetchingData={setFetchingData}
          fetchingData={fetchingData}
          setTotalRecords={setTotalRecords}
          setPageInfo={setPageInfo}
          pageInfo={pageInfo}
          firstLoad={firstLoad}
          setFirstLoad={setFirstLoad}
          setStudentsFiltered={setStudentListWithEmailOverride}
          studentsPagination={studentsPagination}
          setStudentsPagination={setStudentsPagination}
        />
      </Suspense>

      <DataTable
        lazy
        loading={fetchingData}
        stripedRows
        paginator
        rows={filterState.rows}
        rowsPerPageOptions={[5, 10, 25]}
        sortField="name"
        totalRecords={totalRecords}
        sortOrder={filterState.sortOrder}
        filterDisplay="row"
        emptyMessage="No students found."
        value={studentsList}
        scrollable
        first={filterState.first}
        className="w-full"
        onPage={(e: DataTableStateEvent) => {
          setFetchingData(true);
          const newPage: StudentsTableFilterState = {
            ...filterState,
            page: e.page,
            rows: e.rows,
            first: e.first,
            pageCount: e.pageCount!,
            prevPage: filterState.page!,
          };
          setFilterState(newPage);
        }}
        onSort={() => {
          setFetchingData(true);
          setFilterState({ ...filterState, sortOrder: filterState.sortOrder === -1 ? 1 : -1 });
        }}
        paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
      >
        <Column
          sortable
          field="name"
          header="Name"
          filter
          filterElement={() => (
            <MaskedInput>
              <InputText
                placeholder="Search by name"
                value={filterState.filters.name}
                onChange={e => {
                  setFetchingData(true);
                  setFilterState({ ...filterState, filters: { ...filterState.filters, name: e.target.value } });
                }}
              />
            </MaskedInput>
          )}
          body={student => (
            <MaskedStudentData student={student} type={PiiTypes.FullName}>
              {student.name}
            </MaskedStudentData>
          )}
        />
        <Column
          field="emailAddress"
          header="Email"
          filterElement={() => (
            <MaskedInput>
              <InputText
                placeholder="Search by email"
                value={filterState.filters.emailAddress}
                onChange={e => {
                  setFetchingData(true);
                  setFilterState({ ...filterState, filters: { ...filterState.filters, emailAddress: e.target.value } });
                }}
              />
            </MaskedInput>
          )}
          filter
          body={student => (
            <MaskedStudentData student={student} type={PiiTypes.Email}>
              {student.emailAddress}
            </MaskedStudentData>
          )}
        />
        <Column
          field="sites"
          header="Site"
          filter
          filterElement={option =>
            rowFilterTemplate(
              option,
              userRedux?.sites?.map(st => ({ id: st.id, name: st.name })) as { id: string; name: string }[],
            )
          }
          showFilterMenu={false}
          body={element => {
            return <span>{element.site.map((site: { id: string; name: string }) => site.name).join(', ')}</span>;
          }}
        />
        <Column
          field="grades"
          header="Grade"
          filter
          filterElement={option => rowFilterTemplate(option, allGradeLevels as { id: string; name: string }[])}
          showFilterMenu={false}
          body={element => {
            return <span>{element.grade.name}</span>;
          }}
        />
        <Column
          field="classes"
          filterElement={option =>
            rowFilterTemplate(option, classes?.nodes as { id: string; name: string; section: string }[])
          }
          filter
          className="w-2rem"
          header="Class(es)"
          showFilterMenu={false}
        />
        <Column
          className="w-full"
          field="roadmaps"
          filter
          body={element => {
            return <span>{element.currentRoadmap?.name}</span>;
          }}
          filterElement={option =>
            rowFilterTemplate(option, [
              { id: NO_ASSIGNED_ROADMAP, name: NO_ASSIGNED_ROADMAP },
              ...(userRedux?.sites
                ?.map(st => st.targetGroups)
                .flat()
                .filter(
                  (tg, index, arr) => arr.findIndex(element => element?.id === tg?.id) === index,
                ) as TargetGroup[]),
            ])
          }
          header="Assigned Roadmap"
          showFilterMenu={false}
        />
        <Column
          frozen
          alignFrozen="right"
          rowEditor={true}
          headerStyle={{ width: '5%', minWidth: '4rem' }}
          bodyStyle={{ textAlign: 'center' }}
          body={(data: CompleteUser) => (
            <Button
              icon={<SquarePen size={18} aria-hidden style={{ flexShrink: 0 }} />}
              aria-label="Edit the user"
              tooltip="Edit the user"
              rounded
              outlined
              onClick={() => {
                setOpenEnrollStudent(true);
                setSelectedStudent(data);
              }}
            />
          )}
        />
      </DataTable>
      <Suspense>
        <QueryGetAllClasses classes={classes} setClasses={setClasses} />
      </Suspense>
      <Suspense>
        <QueryGetAllGradeLevels setAllGradeLevels={setAllGradeLevels} />
      </Suspense>
    </>
  );
};

export default StudentsDataTable;
